'use client'
import { PublicHeader } from '@/components/public/PublicHeader'
import { HomeEvents } from '@/components/public/HomeEvents'
import { Footer } from '@/components/public/Footer'
import {
  Shield, Users, Database, CheckCircle, FileText,
  AlertCircle, MapPin, Pen,
} from 'lucide-react'
import { useLanguage } from '@/lib/language-context'
import { useEffect } from 'react'

const STATS = [
  { icon: Users,       value: '50,000+', labelKey: 'citizensRegistered' as const },
  { icon: Database,    value: '12,450',  labelKey: 'totalComplaints'    as const },
  { icon: CheckCircle, value: '11,200',  labelKey: 'resolvedComplaints' as const },
  { icon: Shield,      value: '100%',    labelKey: 'secureRecords'      as const },
  { icon: CheckCircle, value: '99.8%',   labelKey: 'verifiedEntries'    as const },
]

export default function HomePage() {
  const { t } = useLanguage()

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => e.isIntersecting && e.target.classList.add('revealed')),
      { threshold: 0.1 }
    )
    document.querySelectorAll('.scroll-reveal').forEach(el => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  return (
    <div className="min-h-screen" style={{ background: '#1a0d04' }}>
      <PublicHeader />

      {/* ============================================================
          HERO SECTION
          Background image contains: Mayor photo, Logo, Pune skyline
          We layer HTML text + cards on top
      ============================================================ */}
      <section
        className="hero-section"
        style={{
          minHeight: '800px',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Background image ── */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url(/background_image_hero.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
          }}
        />



        {/* ════════════════════════════════════════════════
            MAIN 3-COLUMN CONTENT ROW
        ════════════════════════════════════════════════ */}
        <div
          className="relative w-full max-w-7xl mx-auto px-6 hero-main-grid"
          style={{
            zIndex: 2,
            flex: 1,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '0',
            alignItems: 'center',
            paddingTop: '0px',
            paddingBottom: '20px',
          }}
        >
          {/* ─── COL 1: Left — empty (Mayor already in bg image) ─── */}
          <div className="hero-col-left" style={{ minHeight: '400px' }} />

          {/* ─── COL 2: Center — Brand text below logo ─── */}
          <div
            className="hero-col-center anim-fade-in-up anim-delay-200"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              textAlign: 'center',
              paddingTop: '400px', /* Push text down to sit perfectly under the logo in bg image */
              paddingBottom: '24px',
              gap: '6px',
              transform: 'translateX(50px)', /* Push slightly to the right */
            }}
          >
            {/* Decorative ornament */}
            <div style={{ color: '#F39C12', fontSize: '1.1rem', marginBottom: '4px' }}>✦</div>

            {/* "Pune Pratham" */}
            <h1
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 800,
                fontSize: '2.6rem',
                color: '#ffffff',
                lineHeight: 1.1,
                margin: 0,
                letterSpacing: '-0.01em',
                textShadow: '0 2px 12px rgba(0,0,0,0.6)',
              }}
            >
              Pune Pratham
            </h1>

            {/* "Mayor's Initiative" */}
            <div
              style={{
                color: '#F39C12',
                fontWeight: 700,
                fontSize: '1.05rem',
                fontFamily: 'Poppins, sans-serif',
                letterSpacing: '0.01em',
              }}
            >
              Mayor&apos;s Initiative
            </div>

            {/* Tagline */}
            <p
              style={{
                color: 'rgba(255,255,255,0.78)',
                fontSize: '0.88rem',
                lineHeight: 1.65,
                maxWidth: '280px',
                marginTop: '4px',
                textShadow: '0 1px 6px rgba(0,0,0,0.5)',
              }}
            >
              Empowering citizens through<br />transparent and inclusive governance.
            </p>
          </div>

          {/* ─── COL 3: Right — Join Us content ─── */}
          <div
            className="hero-col-right anim-fade-in-right anim-delay-300"
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: '0 0 24px 24px',
              maxWidth: '520px',
              marginLeft: 'auto',
              transform: 'translateX(130px)', /* Push slightly to the right */
            }}
          >
            {/* Eyebrow */}
            <div
              className="anim-fade-in-right anim-delay-100"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'rgba(243,156,18,0.13)',
                border: '1px solid rgba(243,156,18,0.38)',
                borderRadius: '100px',
                padding: '5px 16px',
                marginBottom: '16px',
                alignSelf: 'flex-start',
              }}
            >
              <span
                style={{
                  width: '6px', height: '6px',
                  borderRadius: '50%',
                  background: '#F39C12',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              <span style={{ color: '#F39C12', fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.04em', fontFamily: 'Poppins, sans-serif' }}>
                Mayor&apos;s Official Initiative
              </span>
            </div>

            {/* "Join Us" heading */}
            <h2
              className="hero-join-heading"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 900,
                fontSize: '68px',
                lineHeight: 1.0,
                color: '#ffffff',
                marginBottom: '18px',
                letterSpacing: '-0.02em',
                textShadow: '0 2px 16px rgba(0,0,0,0.5)',
              }}
            >
              Join Us
            </h2>

            {/* Description */}
            <div
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: '1rem',
                lineHeight: 1.9,
                marginBottom: '30px',
                textShadow: '0 1px 6px rgba(0,0,0,0.4)',
              }}
            >
              <p>Register your details securely.</p>
              <p>Get your unique Registration ID instantly.</p>
              <p>Empowering citizens through digital governance.</p>
            </div>

            {/* CTA Buttons */}
            <div
              className="hero-cta-btns"
              style={{ display: 'flex', gap: '16px', flexWrap: 'nowrap' }}
            >
              {/* Primary — Raise Complaint */}
              <a
                href="/raise-complaint"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '10px',
                  background: '#ffffff', color: '#E67E22',
                  height: '58px', padding: '0 28px', borderRadius: '16px',
                  fontWeight: 700, fontSize: '0.95rem', textDecoration: 'none',
                  border: '2px solid transparent', transition: 'all 0.28s ease',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.25)', fontFamily: 'Poppins, sans-serif',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget
                  el.style.background = '#E67E22'; el.style.color = '#ffffff'
                  el.style.transform = 'translateY(-3px)'
                  el.style.boxShadow = '0 14px 36px rgba(230,126,34,0.45)'
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget
                  el.style.background = '#ffffff'; el.style.color = '#E67E22'
                  el.style.transform = 'translateY(0)'
                  el.style.boxShadow = '0 6px 20px rgba(0,0,0,0.25)'
                }}
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                Raise Complaint
              </a>

              {/* Secondary — Join Us */}
              <a
                href="/registration"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '10px',
                  background: 'transparent', color: '#ffffff',
                  height: '58px', padding: '0 28px', borderRadius: '16px',
                  fontWeight: 700, fontSize: '0.95rem', textDecoration: 'none',
                  border: '2px solid rgba(243,156,18,0.65)', transition: 'all 0.28s ease',
                  fontFamily: 'Poppins, sans-serif', whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget
                  el.style.background = 'linear-gradient(135deg, #F39C12, #E67E22)'
                  el.style.borderColor = 'transparent'
                  el.style.transform = 'translateY(-3px)'
                  el.style.boxShadow = '0 14px 36px rgba(230,126,34,0.38)'
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget
                  el.style.background = 'transparent'
                  el.style.borderColor = 'rgba(243,156,18,0.65)'
                  el.style.transform = 'translateY(0)'; el.style.boxShadow = 'none'
                }}
              >
                <Users className="w-5 h-5 flex-shrink-0" />
                Join Us
              </a>

            </div>
          </div>
        </div>

        {/* ════════════════════════════════════════════════
            BOTTOM ROW: Mayor card  +  5 Stats cards
            Sits at the bottom of the hero inside the section
        ════════════════════════════════════════════════ */}
        <div
          className="relative w-full max-w-7xl mx-auto hero-bottom-row"
          style={{
            zIndex: 2,
            display: 'grid',
            gridTemplateColumns: '220px 1fr 1fr 1fr 1fr 1fr',
            gap: '12px',
            padding: '0 24px 60px',
          }}
        >
          {/* Mayor identity card */}
          <div
            className="anim-fade-in-up anim-delay-400"
            style={{
              background: 'linear-gradient(135deg, #2D1B0F, #1a0d04)',
              borderLeft: '4px solid #E67E22',
              borderRadius: '12px',
              padding: '16px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            }}
          >
            <Pen className="w-5 h-5 flex-shrink-0" style={{ color: '#F39C12' }} />
            <div>
              <div style={{ color: '#ffffff', fontWeight: 700, fontSize: '0.9rem', fontFamily: 'Poppins, sans-serif', lineHeight: 1.2 }}>
                Mrs. Manjusha Nagpure
              </div>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', marginTop: '2px' }}>
                Hon&apos;ble Mayor, Pune
              </div>
            </div>
          </div>

          {/* 5 Statistics cards */}
          {STATS.map((item, i) => (
            <StatCard key={item.labelKey} item={item} t={t} delay={i * 80} />
          ))}
        </div>
      </section>

      {/* ===== ABOUT / VISION SECTION ===== */}
      <section className="py-20 px-6 max-w-7xl mx-auto" style={{ background: '#1a0d04' }}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-white scroll-reveal">
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-[#F39C12]" style={{ fontFamily: 'Poppins, sans-serif' }}>Our Vision</h3>
            <p className="text-white/80 leading-relaxed text-sm">
              Pune Pratham aims to build a comprehensive, secure, and scalable database for all citizens. By leveraging cutting-edge technology, we ensure that government services are accessible, transparent, and efficient for everyone.
            </p>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-[#F39C12]" style={{ fontFamily: 'Poppins, sans-serif' }}>Why Choose Us?</h3>
            <ul className="space-y-3 text-white/80 text-sm">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#E67E22] shrink-0 mt-0.5" />
                <span><strong className="text-white">Secure Records:</strong> We employ government-grade encryption to protect your data.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#E67E22] shrink-0 mt-0.5" />
                <span><strong className="text-white">Instant Registration:</strong> Get your unique Registration ID immediately upon submission.</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#E67E22] shrink-0 mt-0.5" />
                <span><strong className="text-white">Verified Entries:</strong> Our robust validation system ensures high data accuracy.</span>
              </li>
            </ul>
          </div>
          
          <div className="space-y-4">
            <h3 className="text-2xl font-bold text-[#F39C12]" style={{ fontFamily: 'Poppins, sans-serif' }}>Our Commitment</h3>
            <p className="text-white/80 leading-relaxed text-sm">
              We are committed to continuous improvement, working closely with district administrators and state officials to expand our reach and enhance the platform's capabilities for a digital future.
            </p>
          </div>
        </div>
      </section>

      {/* ===== HOME EVENTS SECTION ===== */}
      <HomeEvents />

      <div className="flex justify-center pb-16" style={{ background: '#1a0d04' }}>
        <a
          href="/registration"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '10px',
            background: 'linear-gradient(135deg, #F39C12, #E67E22)', color: '#ffffff',
            height: '58px', padding: '0 36px', borderRadius: '16px',
            fontWeight: 700, fontSize: '1.05rem', textDecoration: 'none',
            border: 'none', transition: 'all 0.28s ease',
            boxShadow: '0 8px 24px rgba(230,126,34,0.3)',
            fontFamily: 'Poppins, sans-serif',
          }}
          onMouseEnter={e => {
            const el = e.currentTarget
            el.style.transform = 'translateY(-3px)'
            el.style.boxShadow = '0 14px 36px rgba(230,126,34,0.45)'
          }}
          onMouseLeave={e => {
            const el = e.currentTarget
            el.style.transform = 'translateY(0)'
            el.style.boxShadow = '0 8px 24px rgba(230,126,34,0.3)'
          }}
        >
          <FileText className="w-5 h-5 flex-shrink-0" />
          Go to Registration
        </a>
      </div>

      <Footer />
    </div>
  )
}

function StatCard({ item, t, delay }: { item: typeof STATS[0]; t: Function; delay: number }) {
  return (
    <div
      className="stat-card anim-fade-in-up"
      style={{
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(243,156,18,0.25)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '18px',
        padding: '18px 12px',
        textAlign: 'center',
        animationDelay: `${0.5 + delay / 1000}s`,
        opacity: 0,
        boxShadow: '0 6px 28px rgba(0,0,0,0.28)',
      }}
    >
      <item.icon className="mx-auto mb-2" style={{ color: '#F39C12', width: '24px', height: '24px' }} />
      <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '1.45rem', color: '#ffffff', lineHeight: 1, marginBottom: '5px' }}>
        {item.value}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.58)', fontSize: '0.7rem', fontWeight: 500 }}>
        {t(item.labelKey)}
      </div>
    </div>
  )
}
